import posthog from "posthog-js";

// --- Captured at module load (before React useEffects clean the URL) ---
const ORIGINAL_LP_PARAM: string | null =
  typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("lp")
    : null;

// --- Known referrer domains mapped to traffic sources ---
const KNOWN_REFERRERS: Record<string, string> = {
  "google.com": "google",
  "google.co.uk": "google",
  "bing.com": "bing",
  "linkedin.com": "linkedin",
  "facebook.com": "facebook",
  "fb.com": "facebook",
  "twitter.com": "twitter",
  "x.com": "twitter",
  "t.co": "twitter",
};

// --- Variant name mapping ---
const VARIANT_NAMES: Record<string, string> = {
  A: "Technical",
  B: "Business",
  C: "Response",
};

// --- Traffic source detection ---
function detectTrafficSource(): string | null {
  if (typeof window === "undefined") return null;

  // 1. Check utm_source parameter (before lp is cleaned, other params remain)
  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get("utm_source");
  if (utmSource) return utmSource;

  // 2. Check referrer domain
  const referrer = document.referrer;
  if (referrer) {
    try {
      const referrerHost = new URL(referrer).hostname.replace(/^www\./, "");
      for (const [domain, source] of Object.entries(KNOWN_REFERRERS)) {
        if (referrerHost === domain || referrerHost.endsWith(`.${domain}`)) {
          return source;
        }
      }
      return "referral";
    } catch {
      // Invalid referrer URL, fall through
    }
  }

  // 3. Fallback
  return "direct";
}

// --- Synchronous variant reader (for non-React contexts like Contact page) ---
export function getStoredVariant(): string | null {
  try {
    const stored = localStorage.getItem("landingVariant");
    if (stored) {
      const { variant, expiry } = JSON.parse(stored) as {
        variant: string;
        expiry: number;
      };
      if (new Date().getTime() < expiry) return variant;
    }
  } catch {
    // localStorage unavailable or corrupt
  }
  return null;
}

// --- Common properties builder ---
function getCommonProperties(
  variant: string
): Record<string, string | null> {
  return {
    variant,
    variant_name: VARIANT_NAMES[variant] ?? "Unknown",
    page_path: typeof window !== "undefined" ? window.location.pathname : "",
    traffic_source: detectTrafficSource(),
    campaign_lp_param: ORIGINAL_LP_PARAM,
  };
}

// --- Safe PostHog capture wrapper ---
function safeCapture(
  eventName: string,
  properties: Record<string, string | null>
): void {
  try {
    posthog.capture(eventName, properties);
  } catch {
    // PostHog not initialized (consent not yet given) — event silently dropped
  }
}

// --- Safe Matomo capture wrapper ---
function safeMatomoTrack(
  category: string,
  action: string,
  label?: string,
  value?: number
): void {
  try {
    if (typeof window === "undefined") return;
    const _paq = (window as { _paq?: unknown[][] })._paq;
    if (!Array.isArray(_paq)) return;

    const args: unknown[] = ["trackEvent", category, action];
    if (label !== undefined) args.push(label);
    if (value !== undefined) args.push(value);
    _paq.push(args as unknown[]);
  } catch {
    // Matomo not available
  }
}

const MATOMO_CATEGORY = "Landing Page";

// ============================================================
// Event tracking functions — each fires both PostHog + Matomo
// ============================================================

export function trackLandingVariantViewed(variant: string): void {
  const props = getCommonProperties(variant);
  safeCapture("landing_variant_viewed", props);
  safeMatomoTrack(MATOMO_CATEGORY, "variant_viewed", variant);
}

export function trackLandingCtaClicked(
  variant: string,
  ctaText: string,
  location: string,
  destination: string
): void {
  const props = {
    ...getCommonProperties(variant),
    cta_text: ctaText,
    location,
    destination,
  };
  safeCapture("landing_cta_clicked", props);
  safeMatomoTrack(
    MATOMO_CATEGORY,
    "cta_clicked",
    `variant=${variant} location=${location} text=${ctaText} href=${destination}`
  );
}

export function trackServiceCardClicked(
  variant: string,
  serviceName: string,
  section: string
): void {
  const props = {
    ...getCommonProperties(variant),
    service_name: serviceName,
    section,
  };
  safeCapture("landing_service_card_clicked", props);
  safeMatomoTrack(
    MATOMO_CATEGORY,
    "service_card_clicked",
    `variant=${variant} service=${serviceName} section=${section}`
  );
}

export function trackPricingCardClicked(
  variant: string,
  planName: string,
  section: string
): void {
  const props = {
    ...getCommonProperties(variant),
    plan_name: planName,
    section,
  };
  safeCapture("landing_pricing_card_clicked", props);
  safeMatomoTrack(MATOMO_CATEGORY, "pricing_card_clicked", planName);
}

export function trackFaqOpened(variant: string, question: string): void {
  if (!question) return;
  const props = {
    ...getCommonProperties(variant),
    question,
  };
  safeCapture("landing_faq_opened", props);
  safeMatomoTrack(MATOMO_CATEGORY, "faq_opened", question);
}

export function trackContactFormStarted(variant: string): void {
  const props = getCommonProperties(variant);
  safeCapture("contact_form_started", props);
  safeMatomoTrack(MATOMO_CATEGORY, "contact_form_started", variant);
}

export function trackContactFormSubmitted(variant: string): void {
  const props = getCommonProperties(variant);
  safeCapture("contact_form_submitted", props);
  safeMatomoTrack(MATOMO_CATEGORY, "contact_form_submitted", variant);
}
